import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type ProjRow = { id: string; project_code: string; project_name: string; grants: string[]; explicitly_set: boolean; obligation_count: number; };
type Data = { projects: ProjRow[]; available_programs: string[] };

export default function GrantAssignmentsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Data>({ queryKey: ['grant-assignments'], queryFn: () => api.get('/bead/grant-assignments') });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [addFor, setAddFor] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const save = useMutation({
    mutationFn: (v: { id: string; grants: string[] }) => api.put(`/bead/projects/${v.id}/grants`, { grants: v.grants }),
    onMutate: (v) => setBusy(v.id),
    onSuccess: (res: any) => { setMsg(`${res.grants.join(', ')} - ${res.spawned} new obligation(s) spawned (total ${res.obligation_count}).`); qc.invalidateQueries({ queryKey: ['grant-assignments'] }); },
    onError: (e: any) => setMsg(`Error: ${e?.message ?? e}`),
    onSettled: () => { setBusy(null); setAddFor(null); setSearch(''); },
  });
  const add = (p: ProjRow, g: string) => { if (p.grants.includes(g)) return; save.mutate({ id: p.id, grants: [...p.grants, g] }); };
  const remove = (p: ProjRow, g: string) => { const next = p.grants.filter((x) => x !== g); if (next.length === 0) { setMsg('A project must keep at least one grant.'); return; } save.mutate({ id: p.id, grants: next }); };

  if (isLoading) return <div className="card"><div className="card__body">Loading...</div></div>;
  const allProgs = data?.available_programs ?? [];

  return (
    <div style={{ maxWidth: 1000 }}>
      <div className="page__header"><div>
        <h1 className="page__title">Compliance - Grant Assignments</h1>
        <div className="page__subtitle">The grant program(s) a project is built under drive which compliance obligations the permit page auto-generates. Projects inherit these from the funding selected on the plan; use this page to override. Adding a grant spawns its obligations immediately; removing never deletes obligations that already have evidence.</div>
      </div></div>
      {msg && <div style={{ background:'#ecfdf5', border:'1px solid #6ee7b7', color:'#065f46', borderRadius:6, padding:'8px 12px', marginBottom:12, fontSize:13 }}>{msg}</div>}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {(data?.projects ?? []).map((p) => {
          const options = allProgs.filter((g) => !p.grants.includes(g) && g.toLowerCase().includes(search.toLowerCase()));
          const hasUnassigned = allProgs.some((g) => !p.grants.includes(g));
          return (
            <div key={p.id} style={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:6, padding:'10px 12px', opacity: busy===p.id?0.5:1 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                <div style={{ minWidth:200 }}>
                  <div style={{ fontWeight:600 }}>{p.project_code}</div>
                  <div style={{ fontSize:11, color:'#94a3b8' }}>{p.project_name}{!p.explicitly_set && <span style={{ marginLeft:6, color:'#cbd5e1' }}>(inherited)</span>}</div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', flex:1 }}>
                  {p.grants.map((g) => (
                    <span key={g} style={{ display:'inline-flex', alignItems:'center', gap:4, background:'#eef2ff', color:'#4338ca', borderRadius:12, padding:'2px 8px', fontSize:12, fontWeight:600 }}>
                      {g}<button onClick={() => remove(p, g)} disabled={busy===p.id} title="Remove" style={{ border:'none', background:'none', color:'#6366f1', cursor:'pointer', fontSize:14, lineHeight:1, padding:0 }}>x</button>
                    </span>
                  ))}
                  {addFor === p.id ? (
                    <span style={{ position:'relative' }}>
                      <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setTimeout(() => { setAddFor(null); setSearch(''); }, 150)} placeholder="search grants..." style={{ padding:'3px 8px', fontSize:12, border:'1px solid #cbd5e1', borderRadius:4, width:160 }} />
                      {options.length > 0 && (
                        <div style={{ position:'absolute', top:'100%', left:0, zIndex:10, background:'#fff', border:'1px solid #cbd5e1', borderRadius:4, marginTop:2, minWidth:160, boxShadow:'0 4px 12px rgba(0,0,0,0.12)', maxHeight:200, overflowY:'auto' }}>
                          {options.map((g) => (
                            <div key={g} onMouseDown={() => add(p, g)} style={{ padding:'5px 10px', fontSize:12, cursor:'pointer' }}>{g}</div>
                          ))}
                        </div>
                      )}
                    </span>
                  ) : (
                    hasUnassigned && <button onClick={() => { setAddFor(p.id); setSearch(''); }} disabled={busy===p.id} style={{ border:'1px dashed #cbd5e1', background:'none', color:'#64748b', borderRadius:12, padding:'2px 10px', fontSize:12, cursor:'pointer' }}>+ Add grant</button>
                  )}
                </div>
                <div style={{ fontSize:12, color:'#475569', minWidth:90, textAlign:'right' }}>{p.obligation_count} obligations</div>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ fontSize:10, color:'#94a3b8', marginTop:10 }}>
        Showing grant families with compliance content ({allProgs.join(', ')}). The full funding catalog (hundreds of programs) is selected per-territory on the Builder; those map into these families automatically.
      </div>
    </div>
  );
}
